import { ComponentType } from '@angular/cdk/portal';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { defer, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, tap, timeout } from 'rxjs/operators';
import { PublicRecordApi } from 'src/app/_shared/api/public-record.api';
import { ActivateModalComponent } from 'src/app/_shared/component/activate-account/activate-modal.component';
import { SignupDialogComponent } from 'src/app/_shared/component/login-signup/signup-dialog.component';
import { UserState } from 'src/app/_shared/state/user.state';
import { AppState } from 'src/app/app.state';
import { SignupAgentLoanofficerComponent } from 'src/app/signup/component/signup-agent-loanofficer/signup-agent-loanofficer.component';
import { environment } from 'src/environments/environment';
import { AuthToken } from '../../interface/auth.interface';
import { CookieStorageService } from '../cookie-storage.service';
import { SocketService } from '../socket/socket.service';

interface LoginDialogResult {
  success?: boolean;
  cancelled?: boolean;
  response?: any;
}

interface RefreshedTokens {
  idToken?: string;
  accessToken?: string;
  refreshToken?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HttpInterceptorService implements HttpInterceptor {
  /**
   * Prevents multiple activation/login error dialogs from opening at once.
   */
  private isActiveError = false;

  private isRefreshing = false;
  // Shared by HTTP 401 handling AND Socket.IO authentication recovery.
  private authentication$?: Observable<boolean>;

  /**
   * Authentication endpoints must never start another refresh flow.
   * Otherwise userAuthenticate/authRefresh failures can create a loop.
   */
  private readonly tokenUpdateUrls = [
    '/authRefresh',
    '/userAuthenticate',
    '/signupUser',
    '/socialMediaSignIn',
  ];

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private router: Router,
    private snackbar: MatSnackBar,
    private cookieStorageService: CookieStorageService,
    private appState: AppState,
    private userState: UserState,
    private publicRecordApi: PublicRecordApi,
    private socketService: SocketService
  ) {
    this.socketService.registerAuthenticationRecovery(() => this.getAuthentication());
  }

  /**
   * Adds available tokens, forwards the request and handles authentication
   * failures without changing the behaviour of non-authentication requests.
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    /**
     * MLS board requests use a separately generated token.
     * Preserve the existing special handling for this endpoint.
     */
    if (request.url.includes('mls/v3/GetMLSBoards')) {
      return this.handleMlsRequest(request, next);
    }

    const authenticatedRequest = this.addCurrentTokens(request);

    return next.handle(authenticatedRequest).pipe(
      /**
       * Authentication APIs return updated tokens in response headers.
       * Save them before component subscribers receive the response.
       */
      tap((event: HttpEvent<any>) => {
        if (!(event instanceof HttpResponse)) {
          return;
        }

        if (this.isTokenUpdateUrl(event.url || '')) {
          this.updateTokensFromResponse(event, request);
        }
      }),

      catchError((error: HttpErrorResponse) => {
        const isAuthenticationEndpoint = this.isTokenUpdateUrl(request.url);

        /**
         * Retry normal protected requests after refresh/login.
         *
         * Authentication endpoints are intentionally excluded to prevent:
         * userAuthenticate -> 401 -> authRefresh -> login -> infinite loop.
         */
        if (error.status === 401 && !isAuthenticationEndpoint) {
          return this.handle401(authenticatedRequest, next);
        }

        /**
         * Let the component that called userAuthenticate/authRefresh handle
         * authentication-endpoint errors. In particular, authRefresh errors
         * are handled by the outer handle401 flow.
         */
        if (isAuthenticationEndpoint) {
          return throwError(error);
        }

        /**
         * Preserve the existing handling for unverified users, generic
         * 401/403 responses and server errors.
         */
        this.handleAuthErrors(error);
        return throwError(error);
      })
    );
  }

  /**
   * Checks whether a URL belongs to an authentication/token endpoint.
   */
  private isTokenUpdateUrl(url: string): boolean {
    return this.tokenUpdateUrls.some((tokenUrl) => url.includes(tokenUrl));
  }

  /**
   * Adds the latest token cookies while preserving all request-specific
   * headers such as Content-Type, custom headers and multipart boundaries.
   */
  private addCurrentTokens(request: HttpRequest<any>): HttpRequest<any> {
    const idToken = this.cookieStorageService.getCookieValue('idToken');
    const accessToken = this.cookieStorageService.getCookieValue('accessToken');
    const refreshToken = this.cookieStorageService.getCookieValue('refreshToken');

    let headers = request.headers
      .delete('id-token')
      .delete('access-token')
      .set('Accept', 'application/json');

    if (idToken) {
      headers = headers.set('id-token', idToken);
    }

    if (accessToken) {
      headers = headers.set('access-token', accessToken);
    }

    /**
     * Send refresh-token only to authRefresh.
     */
    if (request.url.includes('authRefresh') && refreshToken) {
      headers = headers.set('refresh-token', refreshToken);
    }

    return request.clone({ headers });
  }

  /**
   * Preserves the existing MLS token flow.
   */
  private handleMlsRequest(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.publicRecordApi.getToken().pipe(
      switchMap((response: any) => {
        const token = response?.data?.token;

        let headers = request.headers;

        if (token) {
          headers = headers.set('access-token', token);
        }

        return next.handle(
          request.clone({
            headers,
          })
        );
      })
    );
  }

  /**
   * Handles a protected request that failed with 401.
   *
   * Flow:
   * 1. Try authRefresh.
   * 2. If refresh succeeds, save tokens and retry the original request.
   * 3. If refresh token is missing/invalid, open the correct login dialog.
   * 4. Wait for `{ success: true }`.
   * 5. Retry the original request with the latest token cookies.
   */
  private handle401(
    failedRequest: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // A parallel request may already have refreshed the rejected token.
    const currentId = this.cookieStorageService.getCookieValue('idToken');
    const currentAccess = this.cookieStorageService.getCookieValue('accessToken');
    const idChanged = currentId && currentId !== failedRequest.headers.get('id-token');
    const accessChanged = currentAccess && currentAccess !== failedRequest.headers.get('access-token');
    if (!this.authentication$ && (idChanged || accessChanged)) {
      return this.retryWithLatestTokens(failedRequest, next);
    }
    return this.getAuthentication().pipe(
      switchMap(() => this.retryWithLatestTokens(failedRequest, next))
    );
  }

  private getAuthentication(): Observable<boolean> {
    if (!this.authentication$) {
      this.authentication$ = defer(() => {
        this.isRefreshing = true;
        return this.ensureAuthenticated();
      }).pipe(
        finalize(() => {
          this.isRefreshing = false;
          this.authentication$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.authentication$;
  }

  /**
   * Ensures that valid tokens are available.
   *
   * It first tries authRefresh. Only refresh-token-related 401/403 failures
   * open the interactive login dialog. Other refresh errors are passed to the
   * existing error handler.
   */
  private ensureAuthenticated(): Observable<boolean> {
    return this.callRefreshToken().pipe(
      switchMap((refreshResponse: any) => {
        /**
         * Support both possible API shapes:
         * - { idToken, accessToken, refreshToken }
         * - { data: { idToken, accessToken, refreshToken } }
         */
        const tokens: RefreshedTokens = refreshResponse?.data || refreshResponse || {};

        if (!tokens.idToken || !tokens.accessToken) {
          return throwError(new HttpErrorResponse({
            status: 502,
            error: { message: 'Refresh response did not contain authentication tokens.' },
          }));
        }
        this.saveTokens(tokens);
        this.socketService.tokensUpdated();
        return of(true);
      }),

      catchError((refreshError: HttpErrorResponse) => {
        if (!this.isRefreshAuthenticationFailure(refreshError)) {
          this.handleAuthErrors(refreshError);
          return throwError(refreshError);
        }

        /**
         * Refresh token is missing/expired. Open:
         * - /chat: SignupAgentLoanofficerComponent
         * - every other route: SignupDialogComponent
         */
        return this.openSigninDialog(true).pipe(
          switchMap((loginResult: LoginDialogResult) => {
            if (!loginResult?.success) {
              return throwError({
                status: 401,
                message: 'Login was cancelled.',
              });
            }

            /**
             * userAuthenticate response headers are saved by intercept()
             * before the dialog component receives its success callback.
             */
            const idToken = this.cookieStorageService.getCookieValue('idToken');
            const accessToken = this.cookieStorageService.getCookieValue('accessToken');

            if (!idToken || !accessToken) {
              return throwError({
                status: 401,
                message: 'Login succeeded, but authentication tokens were not received.',
              });
            }

            return of(true);
          })
        );
      })
    );
  }

  /**
   * Returns true for refresh-token failures that require interactive login.
   */
  private isRefreshAuthenticationFailure(error: HttpErrorResponse): boolean {
    const httpStatus = error.status;
    const backendStatusCode = error.error?.statusCode;
    const message = error.error?.message || '';

    return (
      httpStatus === 401 ||
      httpStatus === 403 ||
      backendStatusCode === 401 ||
      backendStatusCode === 403 ||
      /refresh token missing/i.test(message) ||
      /refresh token expired/i.test(message) ||
      /invalid refresh token/i.test(message)
    );
  }

  /**
   * Calls authRefresh with the current refresh-token cookie.
   *
   * This request passes through the interceptor, but authRefresh is excluded
   * from handle401, preventing recursion when the refresh token is missing.
   */
  private callRefreshToken(): Observable<RefreshedTokens> {
    const refreshToken = this.cookieStorageService.getCookieValue('refreshToken') || '';

    return this.http.post<RefreshedTokens>(
      environment.prospektrBackendBaseURL.replace(/\/$/, '') + '/authRefresh',
      {},
      {
        headers: new HttpHeaders({
          'refresh-token': refreshToken,
        }),
      }
    ).pipe(timeout(20000));
  }

  /**
   * Stores refreshed tokens returned in the authRefresh response body.
   */
  private saveTokens(tokens: RefreshedTokens): void {
    if (tokens?.idToken) {
      this.cookieStorageService.setCookie('idToken', tokens.idToken, 7);
    }

    if (tokens?.accessToken) {
      this.cookieStorageService.setCookie('accessToken', tokens.accessToken, 7);
    }

    if (tokens?.refreshToken) {
      this.cookieStorageService.setCookie('refreshToken', tokens.refreshToken, 7);
    }

    if (tokens && Object.keys(tokens).length) {
      this.appState.authTokenValue = {
        ...this.appState.authTokenValue,
        ...tokens,
      };
    }
  }

  /**
   * Saves tokens returned in successful authentication response headers.
   */
  private updateTokensFromResponse(
    response: HttpResponse<any>,
    request: HttpRequest<any>
  ): void {
    const idTokenNew = response.headers.get('id-token');
    const accessTokenNew = response.headers.get('access-token');
    const refreshTokenNew = response.headers.get('refresh-token');

    const oldIdToken = this.cookieStorageService.getCookieValue('idToken');
    const oldAccessToken = this.cookieStorageService.getCookieValue('accessToken');
    const oldRefreshToken = this.cookieStorageService.getCookieValue('refreshToken');

    // Enable these when token debugging is required.
    // this.logTokenChange('idToken', oldIdToken, idTokenNew, request, response);
    // this.logTokenChange('accessToken', oldAccessToken, accessTokenNew, request, response);
    // this.logTokenChange('refreshToken', oldRefreshToken, refreshTokenNew, request, response);

    if (idTokenNew) {
      this.cookieStorageService.setCookie('idToken', idTokenNew, 7);
    }

    if (accessTokenNew) {
      this.cookieStorageService.setCookie('accessToken', accessTokenNew, 7);
    }

    if (refreshTokenNew) {
      this.cookieStorageService.setCookie('refreshToken', refreshTokenNew, 7);
    }

    const newAuthToken: Partial<AuthToken> = {};

    if (idTokenNew && idTokenNew !== oldIdToken) {
      newAuthToken.idToken = idTokenNew;
    }

    if (accessTokenNew && accessTokenNew !== oldAccessToken) {
      newAuthToken.accessToken = accessTokenNew;
    }

    if (refreshTokenNew && refreshTokenNew !== oldRefreshToken) {
      newAuthToken.refreshToken = refreshTokenNew;
    }

    if (Object.keys(newAuthToken).length) {
      this.appState.authTokenValue = {
        ...this.appState.authTokenValue,
        ...newAuthToken,
      };
    }

    if (request.url.includes('/authRefresh')) {
      this.socketService.tokensUpdated();
    } else if (idTokenNew) {
      this.socketService.connect();
      this.socketService.tokensUpdated();
    }
  }

  /**
   * Retries a failed request with the latest token cookies while preserving
   * all original request headers and payload.
   */
  private retryWithLatestTokens(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const idToken = this.cookieStorageService.getCookieValue('idToken');
    const accessToken = this.cookieStorageService.getCookieValue('accessToken');

    let headers = request.headers
      .delete('id-token')
      .delete('access-token');

    if (idToken) {
      headers = headers.set('id-token', idToken);
    }

    if (accessToken) {
      headers = headers.set('access-token', accessToken);
    }

    return next.handle(
      request.clone({
        headers,
      })
    );
  }

  /**
   * Preserves existing handling for errors outside the automatic 401 retry.
   */
  private handleAuthErrors(error: HttpErrorResponse): void {
    if (this.isActiveError) {
      return;
    }

    const httpStatus = error.status;
    const backendStatusCode = error.error?.statusCode;
    const backendStatus = error.error?.status;
    const message = error.error?.message || '';

    console.error('❌ HTTP ERROR', {
      url: error.url,
      httpStatus,
      backendStatusCode,
      backendStatus,
      message,
    });

    /**
     * Preserve email-verification handling.
     */
    if (
      backendStatus === 'UNVERIFIED' ||
      (this.userState.userDetails?.emailVerificationDate === null &&
        httpStatus === 403 &&
        backendStatus === 'UNAUTHORIZED')
    ) {
      this.openActivateDialog();
      return;
    }

    /**
     * Preserve clearing of invalid auth state.
     */
    if ((httpStatus === 401 || backendStatusCode === 401) && !this.isRefreshing) {
      this.appState.authTokenValue = null;
      this.userState.userDetails = null;
    }

    /**
     * Preserve generic sign-in-dialog handling.
     *
     * This is not the automatic retry flow, so openedFromInterceptor is false
     * and the login components keep their normal navigation behaviour.
     */
    if ([401, 403].includes(httpStatus)) {
      this.openSigninDialog(false).subscribe();
      return;
    }

    /**
     * Preserve generic server-error snackbar handling.
     */
    if (httpStatus === 500) {
      if (/token expired/i.test(message) || /refresh token missing/i.test(message)) {
        return;
      }

      this.snackbar.open(message || 'Something went wrong. Please try again later.', '', {
        duration: 4000,
        panelClass: ['snackbar-warning'],
        verticalPosition: 'top',
      });
    }
  }

  /**
   * Preserves the existing account-activation dialog.
   */
  private openActivateDialog(): void {
    if (this.isActiveError) {
      return;
    }

    const alreadyOpen = this.dialog.openDialogs.some(
      (dialogRef) => dialogRef.componentInstance instanceof ActivateModalComponent
    );

    if (alreadyOpen) {
      return;
    }

    this.isActiveError = true;
    sessionStorage.setItem('redirectUrl', this.router.url);

    const dialogRef = this.dialog.open(ActivateModalComponent, {
      width: '385px',
      disableClose: true,
      hasBackdrop: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isActiveError = false;
    });
  }

  /**
   * Opens or reuses the correct login dialog.
   *
   * Route selection is preserved:
   * - /chat -> SignupAgentLoanofficerComponent
   * - all other routes -> SignupDialogComponent
   *
   * When `openedFromInterceptor` is true, the dialog returns:
   * - { success: true, response } after authentication
   * - { success: false, cancelled: true } when cancelled
   */
  private openSigninDialog(openedFromInterceptor: boolean): Observable<LoginDialogResult> {
    const existingDialog = this.dialog.openDialogs.find(
      (dialogRef) =>
        dialogRef.componentInstance instanceof SignupAgentLoanofficerComponent ||
        dialogRef.componentInstance instanceof SignupDialogComponent
    );

    /**
     * Reuse an already-open login dialog to prevent duplicates.
     */
    if (existingDialog) {
      if (openedFromInterceptor && existingDialog.componentInstance) {
        existingDialog.componentInstance.data = {
          ...(existingDialog.componentInstance.data || {}),
          openedFromInterceptor: true,
        };
      }

      return existingDialog.afterClosed();
    }

    this.isActiveError = true;
    sessionStorage.setItem('redirectUrl', this.router.url);

    const currentPath = this.router.url.split('?')[0];

    const dialogComponent: ComponentType<any> =
      currentPath === '/chat'
        ? SignupAgentLoanofficerComponent
        : SignupDialogComponent;

    const dialogRef = this.dialog.open(dialogComponent, {
      width: '500px',
      disableClose: true,
      hasBackdrop: true,
      data: {
        openedFromInterceptor,
      },
    });

    return dialogRef.afterClosed().pipe(
      tap(() => {
        this.isActiveError = false;
      })
    );
  }

  /**
   * Optional token-debugging helper retained from the original interceptor.
   */
  private logTokenChange(
    type: 'idToken' | 'accessToken' | 'refreshToken',
    oldToken: string | null,
    newToken: string | null,
    request: HttpRequest<any>,
    response: HttpResponse<any>
  ): void {
    if (!newToken || oldToken === newToken) {
      return;
    }

    const last5 = (token?: string | null) =>
      token ? token.slice(Math.max(0, token.length - 5)) : '';

    console.group(`🔐 TOKEN UPDATED → ${type}`);
    console.log('➡️ Request:', request.method, request.url);
    console.log('🌍 App Origin:', window.location.origin);
    console.log('🔁 Request Host:', new URL(request.url).host);
    console.log('📥 Status:', response.status);
    console.log('OLD:', last5(oldToken));
    console.log('NEW:', last5(newToken));
    console.trace('Call Stack');
    console.groupEnd();
  }
}